import { useFetchLoyaltySubscription } from "@multicines/services";
import { useCountriesStore } from "@multicines/stores";
import { useMoviesStore } from "@multicines/stores";
import { AGES, getPointsAndVisits, titleCase } from "@multicines/utils";
import { Image } from "artisn-ui-react";
import dayjs from "dayjs";
import LaurelSVG from "images/laurel.svg";
import React, { useMemo } from "react";
import "dayjs/locale/es";

import Styles from "./SelectedMovieDetail.styles";
import { SelectedMovieDetailProps as Props } from "./SelectedMovieDetail.types";
import useI18n from "hooks/useI18n";
import { useAuthStore } from "stores/auth/auth.store";
import { useStoresStore } from "stores/stores/stores.store";

const { ALL_PUBLIC, WITHOUT_SUPERVISION } = AGES;
const { WITH_RESTRICTION, WITH_SUPERVISION } = AGES;

const SelectedMovieDetail: React.FC<Props> = props => {
  const { className, hiddenImage, hiddenPoints } = props;
  const selectedStore = useStoresStore(store => store.selectedStore);
  const [ticketsStore] = selectedStore ?? [];
  const { storeName = "" } = ticketsStore ?? {};
  const selectedMovie = useMoviesStore(store => store.selectedMovie);
  const selectedSession = useMoviesStore(store => store.selectedSession);
  const { title = "", imageUrl, genre, languages } = selectedMovie ?? {};
  const { ageRestriction } = selectedMovie ?? {};
  const { age } = ageRestriction ?? {};
  const allPublic = age === ALL_PUBLIC;
  const t = useI18n();
  const { showtime, screenName = "" } = selectedSession ?? {};
  const isAnonymous = useAuthStore(state => state.isAnonymous);
  const uid = useAuthStore(state => state.uid);
  const { data: membership } = useFetchLoyaltySubscription({
    isAnonymous,
    uid
  });
  const { points } = useMemo(
    () => getPointsAndVisits(membership?.balanceList),
    [membership]
  );
  const selectedCountry = useCountriesStore(state => state.selectedCountry);
  const { locale } = selectedCountry;

  const renderAgeRestriction = () => {
    if (allPublic) {
      return (
        <p className="SelectedMovieDetail__movie__age">{t.movie.allPublic}</p>
      );
    }

    if (!age) return;

    if (age < WITH_SUPERVISION) {
      return (
        <p className="SelectedMovieDetail__movie__age">{`${age} ${t.movie.withSupervision}`}</p>
      );
    }
    if (age === WITHOUT_SUPERVISION) {
      return (
        <p className="SelectedMovieDetail__movie__age">
          {`+${age}`} {t.movie.age}
        </p>
      );
    }
    if (age > WITH_RESTRICTION) {
      return (
        <p className="SelectedMovieDetail__movie__age__restricted">
          {`+${age}`} {t.movie.age}
        </p>
      );
    }
    return (
      <p className="SelectedMovieDetail__movie__age">
        {`+${age}`} {t.movie.age}
      </p>
    );
  };

  const renderImage = () => {
    if (imageUrl) {
      return (
        <>
          <Image
            className="SelectedMovieDetail__image__desktop"
            image={imageUrl}
            alt={title}
            width={112}
          />
          <Image
            className="SelectedMovieDetail__image__no-desktop"
            image={imageUrl}
            alt={title}
            width={56}
          />
        </>
      );
    }

    return (
      <div className="SelectedMovieDetail__placeholder">
        <p className="SelectedMovieDetail__placeholder__text">{title}</p>
      </div>
    );
  };

  const renderPoints = () => {
    if (!membership || hiddenPoints) return null;

    return (
      <>
        <LaurelSVG
          className="SelectedMovieDetail__laurel"
          viewBox="0 0 40 75"
          width={20}
        />
        <div className="SelectedMovieDetail__points">
          <p className="SelectedMovieDetail__points__text">{points}</p>
          <p className="SelectedMovieDetail__points__text">pts</p>
        </div>
        <LaurelSVG
          className="SelectedMovieDetail__laurel SelectedMovieDetail__laurel__right"
          viewBox="0 0 40 75"
          width={20}
        />
      </>
    );
  };

  return (
    <Styles className={`SelectedMovieDetail ${className}`} image={imageUrl}>
      <div className="SelectedMovieDetail__background" />
      <div className="SelectedMovieDetail__body__image">
        <div className="SelectedMovieDetail__header__data">
          {!hiddenImage ? renderImage() : null}
          <div className="SelectedMovieDetail__header__description">
            <div className="SelectedMovieDetail__movie">
              <div className="SelectedMovieDetail__movie__info">
                {genre ? (
                  <p className="SelectedMovieDetail__movie__genre">
                    {titleCase(genre)}
                  </p>
                ) : null}
                <div className="SelectedMovieDetail__movie__language">
                  {languages?.map(language => (
                    <>
                      <span key={language}>{language} · </span>
                    </>
                  ))}
                  {renderAgeRestriction()}
                </div>
              </div>
              <p className="SelectedMovieDetail__movie__title">
                {titleCase(title)}
              </p>
              <p className="SelectedMovieDetail__movie__store-and-screen">
                {titleCase(storeName)} - {titleCase(screenName)}
              </p>
              <p className="SelectedMovieDetail__movie__showtime">
                {titleCase(
                  dayjs(showtime)
                    .locale(locale)
                    .format("ddd DD")
                    .replace(".", "")
                )}
                {" · "}
                {dayjs(showtime).format("HH:mm")}
              </p>
            </div>
            <div className="SelectedMovieDetail__body__points">
              {renderPoints()}
            </div>
          </div>
        </div>
      </div>
    </Styles>
  );
};

export default SelectedMovieDetail;
